import { lazy } from 'react'
import { createBrowserRouter, Navigate, useRouteError, isRouteErrorResponse } from 'react-router-dom'
import { AppLayout } from '@/layouts/AppLayout'
import { AuthLayout } from '@/layouts/AuthLayout'
import { POSLayout } from '@/layouts/POSLayout'
import { ProtectedRoute } from './ProtectedRoute'
import { S } from './lazyWrap'
import { appRouteObjects } from './appRoutes.config'
import { AlertTriangle, RefreshCw } from 'lucide-react'

function RouteErrorPage() {
  const error = useRouteError()
  let message = 'Beklenmeyen bir hata oluştu.'
  let status: number | undefined

  if (isRouteErrorResponse(error)) {
    status = error.status
    message = error.statusText || error.data?.message || message
  } else if (error instanceof Error) {
    message = error.message
  }

  return (
    <div className="flex flex-col items-center justify-center min-h-screen p-8 text-center bg-bg-base">
      <div className="w-16 h-16 rounded-full bg-error/10 flex items-center justify-center mb-4">
        <AlertTriangle className="w-8 h-8 text-error" />
      </div>
      <h2 className="text-2xl font-bold text-text-primary mb-2">
        {status ? `${status} — ` : ''}Bir hata oluştu
      </h2>
      <p className="text-sm text-text-secondary max-w-md mb-6">{message}</p>
      <div className="flex gap-3">
        <button
          onClick={() => window.location.reload()}
          className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg bg-primary text-white hover:bg-primary/90 transition-colors"
        >
          <RefreshCw className="w-4 h-4" />
          Sayfayı Yenile
        </button>
        <button
          onClick={() => (window.location.href = '/')}
          className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg bg-bg-elevated text-text-primary hover:bg-bg-border transition-colors"
        >
          Ana Sayfaya Dön
        </button>
      </div>
    </div>
  )
}

// Lazy loaded pages — auth / pos / desktop (AppLayout dışı).
// AppLayout altındaki korumalı modül route'ları için bkz. ./appRoutes.config.tsx
const LoginPage = lazy(() => import('@/modules/auth/pages/LoginPage'))
const FirmaKayitPage = lazy(() => import('@/modules/auth/pages/FirmaKayitPage'))
const SifreSifirlamaPage = lazy(() => import('@/modules/auth/pages/SifreSifirlamaPage'))
const YeniSifrePage = lazy(() => import('@/modules/auth/pages/YeniSifrePage'))
const EmailDogrulamaPage = lazy(() => import('@/modules/auth/pages/EmailDogrulamaPage'))
const POSPage = lazy(() => import('@/modules/pos/pages/POSPage'))

// Desktop (Tauri) sayfaları
const LicensePage = lazy(() => import('@/modules/desktop/LicensePage'))
const SetupWizard = lazy(() => import('@/modules/desktop/SetupWizard'))

export const router = createBrowserRouter([
  // Kök route: her zaman /login'e yönlendir (AuthLayout /login zaten oturum varsa dashboard'a yönlendirir)
  {
    path: '/',
    element: <Navigate to="/login" replace />,
    errorElement: <RouteErrorPage />,
  },

  // Desktop-only routes (Tauri)
  { path: '/activate', element: <S><LicensePage /></S> },
  { path: '/setup', element: <S><SetupWizard /></S> },

  // Auth routes
  {
    element: <AuthLayout />,
    errorElement: <RouteErrorPage />,
    children: [
      { path: '/login', element: <S><LoginPage /></S> },
      { path: '/kayit', element: <S><FirmaKayitPage /></S> },
      { path: '/sifre-sifirlama', element: <S><SifreSifirlamaPage /></S> },
      { path: '/yeni-sifre', element: <S><YeniSifrePage /></S> },
      { path: '/email-dogrulama', element: <S><EmailDogrulamaPage /></S> },
    ],
  },

  // POS (full-screen, separate layout)
  {
    element: <ProtectedRoute><POSLayout /></ProtectedRoute>,
    errorElement: <RouteErrorPage />,
    children: [
      { path: '/pos', element: <S><POSPage /></S> },
    ],
  },

  // Protected app routes — route tanımları ./appRoutes.config.tsx (tek kaynak)
  {
    element: <ProtectedRoute><AppLayout /></ProtectedRoute>,
    errorElement: <RouteErrorPage />,
    children: appRouteObjects,
  },
])
